import type { PersonaId } from "@petci/fixtures";

/**
 * Home card order contract (H1 / D1). A single per-persona ordered list is the
 * one source of truth for the home dashboard's card sequence. The page renders
 * the visibility-filtered list IN ORDER inside one CSS multi-column container:
 * mobile is a single column (visual order == this list, so the money is in the
 * first viewport — fixes the finding #1 "390px buries balance"), desktop flows
 * the SAME DOM order into balanced columns. Because DOM order always equals this
 * contract at every width, desktop placement can never silently diverge (CR6).
 *
 * Enforced by home-card-order.test.ts (fail-red: scramble any entry).
 */
export type HomeCardId =
  | "balance"
  | "ai-callout"
  | "insights"
  | "context"
  | "hurricane"
  | "usage"
  | "identity"
  | "network-status"
  | "outage-report"
  | "active-requests"
  | "activity"
  | "peak-demand"
  | "hotel-overview"
  | "hotel-ecor"
  | "anomaly-hero"
  | "portfolio-nav";

/** Runtime facts that gate a card's visibility (derived from the active persona). */
export interface HomeCardFlags {
  hurricaneSeason: boolean;
  hasContext: boolean;
  hasInsights: boolean;
  hasUsage: boolean;
  isHotel: boolean;
  hasHotelKpi: boolean;
  hasAnomaly: boolean;
  hasManagedAccounts: boolean;
}

/**
 * Mobile-first order per persona (D2): status strip (page header) → balance
 * (P4: anomaly card first, then balance) → active-alert cards (P3 demand) →
 * AI callout → insights → persona feature cards → hurricane advice → usage
 * chart → identity strip → rail sections. Identity drops below the fold; it
 * earns a slim strip, not a fold card (D3).
 *
 * D-O1 (2026-07-28) — the AI callout is IN this contract, after the money.
 * It used to render as page chrome above the columns, which put a 267px
 * advertisement (measured at 375px in es) between the status strip and the
 * balance: P4's balance landed at y=859 against an 812px fold, P1's pay row at
 * y=867. That is the Phase-1 "390px buries balance" defect returning through
 * the one element that sat OUTSIDE the contract D1 wrote to prevent it. The
 * lesson generalises — a perfect card contract plus one off-contract element
 * above it is the original defect back, so chrome above the cards is itself
 * the thing to audit.
 *
 * D-O4 — DELIBERATE cross-surface divergences, documented per the parity
 * contract ("divergence permitted only when documented as deliberate"):
 *
 *  1. `hurricane` sits LOW here (#5-ish) and HIGH on mobile (#2). Not drift:
 *     web renders a `HurricaneBanner` in the app chrome above every route, so
 *     the banner owns storm AWARENESS and this card is the advice doorway.
 *     Mobile has no banner, so its card IS the awareness surface and must lead.
 *  2. Mobile carries no `network-status`, `activity` or `identity` card. Also
 *     not drift: mobile's island-wide outage pill (chrome) does the first job,
 *     Payment History (F-C2) the second, and the `account` card the third.
 *  3. P3 places `ai-callout` BEFORE `insights`/`hotel-overview` here, and mobile
 *     places its explainer AFTER both (the two also invert `insights` vs
 *     `hotel-overview` between them). Deliberate, per surface: on a desktop
 *     workspace the offer to explain the bill reads best directly after the
 *     peak-demand alert it would explain, while on a phone the operational
 *     cards earn the scarce first screen and the offer follows them. Added
 *     2026-07-29 closing observation O-1 from the R-A/home-order CHECK — the
 *     divergence was ratified in the locked lists but never written down, which
 *     the parity contract's own rule requires.
 */
const ORDER: Record<PersonaId, HomeCardId[]> = {
  P1: ["balance", "ai-callout", "insights", "context", "hurricane", "usage", "identity", "network-status", "outage-report", "active-requests", "activity"],
  P2: ["balance", "ai-callout", "insights", "context", "hurricane", "usage", "identity", "network-status", "outage-report", "active-requests", "activity"],
  P3: ["balance", "peak-demand", "ai-callout", "insights", "hotel-overview", "hotel-ecor", "hurricane", "usage", "identity", "network-status", "outage-report", "active-requests", "activity"],
  P4: ["anomaly-hero", "balance", "ai-callout", "insights", "portfolio-nav", "hurricane", "usage", "identity", "network-status", "outage-report", "active-requests", "activity"],
  // P5 omits `ai-callout` BY CONTRACT (D-O1). The callout opens a bill deep-dive,
  // and this persona has no bill — advertising it would be the H0.7 honesty
  // class. Omission here beats a runtime gate: the contract stays the single
  // place the answer lives, and the page cannot render what the list omits.
  P5: ["balance", "identity", "network-status", "outage-report", "active-requests", "activity"],
};

const VISIBLE: Record<HomeCardId, (f: HomeCardFlags) => boolean> = {
  balance: () => true,
  // Unconditional for the four personas whose lists carry it — all of them have
  // bills. P5's exclusion is the ORDER omission above, not a predicate.
  "ai-callout": () => true,
  insights: (f) => f.hasInsights,
  context: (f) => f.hasContext,
  hurricane: (f) => f.hurricaneSeason,
  usage: (f) => f.hasUsage,
  identity: () => true,
  "network-status": () => true,
  "outage-report": () => true,
  "active-requests": () => true,
  activity: () => true,
  "peak-demand": (f) => f.isHotel,
  "hotel-overview": (f) => f.isHotel,
  "hotel-ecor": (f) => f.isHotel && f.hasHotelKpi,
  "anomaly-hero": (f) => f.hasAnomaly,
  "portfolio-nav": (f) => f.hasManagedAccounts,
};

/**
 * CD-1 — the card ids a persona promotes OUT of the column flow into the
 * full-rail hero band at `lg+`.
 *
 * The commercial personas are desktop-by-design, and their primary object is a
 * PORTFOLIO: at 1920 a four-property manager was being handed the same 437px
 * masonry card a single-account residential customer gets, so "which property
 * needs me this morning" cost N clicks. The band answers it in one glance.
 *
 * Placement is a CONTRACT, not a page decision: this function is the one source
 * for which ids the band owns, so the page cannot render a card in both places
 * (the exclusion is derived from this list, and `home-card-order.test.ts` pins
 * both halves). Residential personas return `[]` — a single balance card at full
 * rail is a billboard, not a workspace, and the masonry already serves them.
 *
 * The band itself is CSS-gated (`hidden lg:block`) and the promoted column
 * entries are `lg:hidden`, so below `lg` every persona renders exactly as it did
 * before this brief — no viewport JS, no hydration branch, nothing to diverge
 * between the static export and the client.
 */
const HERO_BAND: Record<PersonaId, (f: HomeCardFlags) => HomeCardId[]> = {
  P1: () => [],
  P2: () => [],
  // The band carries the resort's projected total, occupancy, demand headroom
  // and all four KPIs, which is the whole of both cards: `hotel-overview` was
  // only a doorway to the dashboard, and `hotel-ecor`'s figure IS the band's
  // first KPI tile. The peak-demand ALERT card deliberately stays in the
  // columns — the band states the condition, the card carries the gauge and the
  // shed hint, which is the drill-in's own badge-plus-tile idiom.
  P3: (f) => (f.isHotel && f.hasHotelKpi ? ["hotel-overview", "hotel-ecor"] : []),
  // The band lists every managed account with its own link, which absorbs the
  // `portfolio-nav` doorway card whole — leaving it below would repeat "4
  // accounts →" underneath a table of those same four accounts.
  P4: (f) => (f.hasManagedAccounts ? ["balance", "portfolio-nav"] : []),
  P5: () => [],
};

export const HOME_CARD_ORDER = ORDER;

/**
 * The ordered, visibility-filtered card ids for a persona — the exact sequence
 * the home page renders (DOM order === this by construction).
 */
export function visibleHomeCards(personaId: PersonaId, flags: HomeCardFlags): HomeCardId[] {
  return ORDER[personaId].filter((id) => VISIBLE[id](flags));
}

/**
 * The card ids the hero band owns for this persona, or `[]` for no band.
 *
 * Visibility-filtered against the same predicates as {@link visibleHomeCards},
 * so the band can never claim a card the persona would not have rendered.
 */
export function heroBandCards(personaId: PersonaId, flags: HomeCardFlags): HomeCardId[] {
  return HERO_BAND[personaId](flags).filter((id) => VISIBLE[id](flags));
}
